import { useEffect, useMemo, useState } from "react";
import { useSession } from "../../app/session";
import { readCircle } from "../../circle/useCircle";
import { matchesFor } from "../../core/circle";
import { readRounds } from "../../core/ops";
import type { FilterAst } from "../../core/filter";
import type { Task } from "../../core/schema";
import { useWorkspaceDocs } from "../../core/useWorkspaceDocs";
import { readPlanner, type PlanItem } from "../../planner/ops";
import { docManager, type DocHandle } from "../../sync/docManager";
import { visibleTasks } from "../shared";

/** One other workspace's tasks for a combined view (ADR 0024). */
export interface Elsewhere {
  id: string;
  name: string;
  handle: DocHandle;
  tasks: Task[];
  planner: Record<string, PlanItem[]>;
}

/**
 * The person's other workspaces with their visible tasks (`filter` applied) and planner, for views that merge
 * them into one grid. `mine`: from a shared workspace only what is the person's (their turn, assigned to them, or
 * to nobody). Recomputed on every change of any of those docs.
 */
export function useElsewhere(
  currentId: string,
  mode: "mine" | "all" | "here",
  filter: FilterAst | undefined,
  nowMs: number,
): Elsewhere[] {
  const workspaces = useSession((s) => s.session?.workspaces) ?? [];
  const me = useSession((s) => s.session?.profile?.id) ?? null;
  const others = mode === "here" ? [] : workspaces.filter((w) => w.id !== currentId);
  const othersKey = others.map((w) => `${w.id}\t${w.name}`).join("\n");
  const handles = useWorkspaceDocs(others.map((w) => w.id));
  const version = useDocsVersion(Object.values(handles));
  const filterKey = JSON.stringify(filter ?? null);
  const minute = Math.floor(nowMs / 60_000);
  return useMemo(() => {
    void version;
    const out: Elsewhere[] = [];
    for (const line of othersKey ? othersKey.split("\n") : []) {
      const [id, name] = line.split("\t");
      const w = { id, name };
      const handle = handles[w.id];
      if (!handle) continue;
      const circle = readCircle(handle.doc);
      const members = circle.members.filter((m) => m.kind !== "viewer").map((m) => m.id);
      const v = visibleTasks(handle.doc, filter, minute * 60_000, { includeDone: true });
      const rounds = readRounds(handle.doc);
      const tasks =
        mode === "mine" && circle.shared && me
          ? v.tasks.filter((x) =>
              matchesFor(x, "me", me, {
                shared: true,
                members,
                subs: rounds[x.id]?.subs,
                fair: rounds[x.id]?.fair,
              }),
            )
          : v.tasks;
      out.push({ id: w.id, name: w.name, handle, tasks, planner: readPlanner(handle.doc) });
    }
    return out;
    // `others` and `filter` are keyed by their content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handles, version, mode, me, filterKey, minute, othersKey]);
}

/** A number that changes whenever one of these docs changes. */
function useDocsVersion(handles: DocHandle[]): number {
  const [version, setVersion] = useState(0);
  const key = handles.map((h) => h.docId).join(",");
  useEffect(() => {
    const offs = (key ? key.split(",") : []).map((id) =>
      docManager.subscribeDoc(id, () => setVersion((v) => v + 1)),
    );
    return () => offs.forEach((off) => off());
  }, [key]);
  return version;
}
